<template>
  <div>
    <InfoCard>
      På denne siden finner du en oversikt over alle dine aktive forvaltningsabonnement, og disse kan du oppgradere
      eller nedgradere til andre abonnementsnivå.
    </InfoCard>

    <div class="mt-4">
      <div>
        <BlockContainer class="p-5">
          <div class="row">
            <div class="col">
              <h5>Aktive forvaltningsabonnement</h5>
            </div>
          </div>

          <List class="mt-5">
            <ListHeader>
              <div class="col">Navn</div>

              <div class="col">Leieobjekt</div>

              <div class="col">Abonnement</div>

              <div class="col text-right">Pris/mnd</div>
            </ListHeader>

            <ListItem v-for="(subscription, i) in landlordProfileSubscriptions" :key="subscription.tenancyId">
              <div class="col text-secondary">{{ subscription.tenant.name }}</div>

              <div class="col">{{ subscription.rentalObject.street }}</div>

              <div class="col subscription text-secondary d-flex align-items-center">
                <span class="col">{{ subscription.subscription.plan }}</span>
                <hl-Button
                  v-if="subscription.subscription.plan === 'basic'"
                  class="col"
                  size="sm"
                  :disabled="loadingPlans"
                  @click="upgradeToPremium(subscription)"
                >
                  <i class="budicon-arrow-top-circle mr-1" />
                  Oppgrader
                </hl-Button>

                <hl-Button
                  v-if="subscription.subscription.plan === 'premium'"
                  class="col"
                  size="sm"
                  :disabled="loadingPlans"
                  @click="downgradeToBasic(subscription)"
                  variant="secondary"
                  outline
                >
                  <i class="budicon-arrow-bottom-circle mr-1" />
                  Nedgrader
                </hl-Button>
              </div>

              <div class="col text-right text-secondary">
                <hl-Currency :amount="subscription.subscription.price"></hl-Currency>
              </div>
            </ListItem>
          </List>
        </BlockContainer>
      </div>
    </div>

    <UpgradeSubscriptionModal
      :show.sync="showUpgradePlanModal"
      :tenantName="currentTenantName"
      :newPrice="currentNewPrice"
      :upgrading="upgrading"
      @click:upgrade="upgradePlanAndClose(currentSubscription)"
    />
    <DowngradeSubscriptionModal
      :show.sync="showDowngradePlanModal"
      :tenantName="currentTenantName"
      :newPrice="currentNewPrice"
      :downgrading="downgrading"
      @click:downgrade="downgradePlanAndClose(currentSubscription)"
    />
  </div>
</template>

<script>
import BlockContainer from '@/components/BlockContainer.vue';
import List from '@/components/UI/list/List.vue';
import ListHeader from '@/components/UI/list/ListHeader.vue';
import ListItem from '@/components/UI/list/ListItem.vue';
import UpgradeSubscriptionModal from '@/components/detailedRentalObject/UpgradeSubscriptionModal.vue';
import DowngradeSubscriptionModal from '@/components/detailedRentalObject/DowngradeSubscriptionModal.vue';
import { mapGetters, mapActions } from 'vuex';
import { TenancyApi } from '@modules/husleieapi2';
import { orderStatusMap as statusMap } from '@/lib/orderStatusHelpers';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import InfoCard from '@/components/InfoCard.vue';

export default {
  components: {
    BlockContainer,
    DowngradeSubscriptionModal,
    List,
    ListHeader,
    ListItem,
    UpgradeSubscriptionModal,
    InfoCard,
    'hl-Button': Button,
    'hl-Date': Date,
    'hl-Currency': Currency,
  },

  props: {
    landlordId: [String, Number],
  },

  data() {
    return {
      currentSubscription: null,
      downgrading: false,
      landlordProfileSubscriptions: [],
      landlordProfileInvoices: [],
      showUpgradePlanModal: false,
      showDowngradePlanModal: false,
      upgrading: false,
      loadingPlans: false,
      plans: null,
      currentUpgradePlan: null,
      currentNewPrice: 0,
      statusMap,
    };
  },

  computed: {
    ...mapGetters(['getLandlordSubscriptions']),

    currentTenantName() {
      if (!this.currentSubscription) {
        return '';
      }
      return this.currentSubscription.tenant.name;
    },
  },

  methods: {
    ...mapActions(['loadLandlordProfileSubscriptions']),

    async changeSubscriptionPlan(tenancyId, planName) {
      const api = new TenancyApi();
      const plan = this.plans.find(plan => plan.name === planName);
      if (!plan) {
        this.$toast(`Kunne ikke finne abonnement ved navn ${planName}.`, 'error');
        return;
      }

      if (planName === 'Premium') {
        this.upgrading = true;
        try {
          await api.upgradeTenancySubscription(this.landlordId, tenancyId, {
            body: {
              subscriptionPlanId: plan.id,
            },
          });
          this.$toast(`Oppgradert til ${planName}.`, 'success');
        } catch (error) {
          console.error(error);
          this.$toast(`Oppgradering til ${planName} feilet.`, 'error');
        }
        await this.loadSubscriptions();
        this.upgrading = false;
      } else if (planName === 'Basic') {
        this.downgrading = true;
        try {
          await api.downgradeTenancySubscription(this.landlordId, tenancyId, {
            body: {
              subscriptionPlanId: plan.id,
            },
          });
          this.$toast(`Nedgradert til ${planName}.`, 'success');
        } catch (error) {
          console.error(error);
          this.$toast(`Nedgradering til ${planName} feilet.`, 'error');
        }
        await this.loadSubscriptions();
        this.downgrading = false;
      }
    },

    async fetchAvailablePlans(landlordId, tenancyId) {
      try {
        const api = new TenancyApi();
        this.plans = await api.getLandlordTenancyAvailableSubscriptions(landlordId, tenancyId);
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke hente abonnement.', 'error');
      }
    },

    async upgradePlanAndClose({ tenancyId }) {
      await this.changeSubscriptionPlan(tenancyId, 'Premium');
      this.showUpgradePlanModal = false;
    },

    async upgradeToPremium(subscription) {
      this.currentSubscription = subscription;
      this.loadingPlans = true;
      await this.fetchAvailablePlans(this.landlordId, subscription.tenancyId);
      this.currentUpgradePlan = this.plans.find(plan => plan.name === 'Premium');
      if (!this.currentUpgradePlan) {
        this.$toast('Kunne ikke finne Premium-abonnement.', 'error');
        return;
      }
      this.currentNewPrice = this.currentUpgradePlan.special_price || this.currentUpgradePlan.price;
      this.loadingPlans = false;
      this.showUpgradePlanModal = true;
    },

    async downgradeToBasic(subscription) {
      this.currentSubscription = subscription;

      this.loadingPlans = true;
      await this.fetchAvailablePlans(this.landlordId, subscription.tenancyId);
      this.currentUpgradePlan = this.plans.find(plan => plan.name === 'Premium');
      if (!this.currentUpgradePlan) {
        this.$toast('Kunne ikke finne Premium-abonnement.', 'error');
        return;
      }
      this.currentNewPrice = this.currentUpgradePlan.special_price || this.currentUpgradePlan.price;
      this.loadingPlans = false;

      this.showDowngradePlanModal = true;
    },

    async downgradePlanAndClose({ tenancyId }) {
      await this.changeSubscriptionPlan(tenancyId, 'Basic');
      this.showDowngradePlanModal = false;
    },

    async loadSubscriptions() {
      try {
        const res = await this.loadLandlordProfileSubscriptions();
        this.landlordProfileSubscriptions = res;
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke laste abonnement.', 'error');
      }
    },
  },
  async mounted() {
    this.loadSubscriptions();
  },
};
</script>

<style scoped lang="scss">
.bank-logo {
  width: 60px;
  height: 60px;
  margin: auto;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
}

.subscription {
  text-transform: capitalize;

  span {
    min-width: 4rem;
  }
  button {
    width: 8rem;
    display: flex;
    align-items: center;
  }
}
</style>
